import { PolicyStore } from '@Policy/store';
import cloneDeep from 'lodash/cloneDeep';

import { Key } from 'react';
import { TreeNode } from 'src/components/Tree/type';
import { getAllKeys } from 'src/components/Tree/utils';
import i18nInst from 'src/i18n';
import {
  checkEmailFormat,
  convertUserGroup,
  findNodeByKey,
  getPolicyName,
  removeElements,
  uniqueArr,
} from 'src/pages/Policy/utils/fns';
import {
  // ExchangeOnlinePolicyItem,
  PolicyItem,
  TargetCapacity,
  TargetGroup,
  TargetUser,
} from 'src/pages/Policy/utils/types';
import { TargetType } from 'src/utils/enums';
import isEmail from 'validator/lib/isEmail';
import { UserApi } from '@Apis';

const INLINE_MAX_USER_NUMBER_IN_O365_GROUP = Number(
  window.INLINE_MAX_USER_NUMBER_IN_O365_GROUP
);

export const createGeneralSlice = (
  setPolicy: any,
  getPolicy: any,
  getAll: any
) => {
  const { t } = i18nInst;
  return {
    handleToRightTargets: (keys: Key[]) => {
      const policy = getPolicy();
      const store = getAll();
      const allAvailableTargets = store.availableTargetsGerneral;
      const allSearchTargets = store.tempVars.searchTargetUserGroups;
      const allTargets = allSearchTargets.length
        ? allSearchTargets
        : allAvailableTargets;
      const originPolicyTarget = policyTargetsSelector(policy);
      const originTargetKeys = originPolicyTarget.map(
        (target: TreeNode) => target.key
      );
      if (keys.length === 0) {
        return;
      }
      // do nothing when origin target is all user
      if (originTargetKeys.length === 1 && originTargetKeys[0] === 'all') {
        return;
      }
      let newPolicyTargets = cloneDeep(policy.policyTarget);
      // add all users
      if (keys.includes('all')) {
        newPolicyTargets = {
          targets: [],
          targetGroups: [],
          targetType: policy.policyTarget.targetType,
          capacity: TargetCapacity.all,
        };
        setPolicy({ policyTarget: newPolicyTargets });
        return;
      }
      // add user groups other than all
      // allTargets[0] is the top layer of the tree: all users
      const nodes = allTargets[0].children;
      const tempKeys = cloneDeep(keys);
      const checkedKeys = []; // the keys that will be added to newTargetKeys
      // if originTargetKeys and its children includes selectedKeys
      let expandOriginTargetKeys: string[] = [];
      originTargetKeys.forEach((key: Key) => {
        const node = findNodeByKey(nodes, key);
        if (node) {
          const allKeys = getAllKeys(key as string | number, node.children);
          expandOriginTargetKeys.push(...(allKeys as string[]));
        }
      });
      expandOriginTargetKeys = uniqueArr(expandOriginTargetKeys);
      // if selcted key existed in originTargetKeys already, remove it from tempKeys
      const duplicatedKeys: Key[] = [];
      tempKeys.forEach((key: Key) => {
        if (expandOriginTargetKeys.includes(key as string)) {
          duplicatedKeys.push(key);
        }
      });
      if (duplicatedKeys.length) {
        removeElements(tempKeys, duplicatedKeys);
      }
      const i = 0;
      while (i < tempKeys.length) {
        const key = tempKeys[i];
        // find this node by key
        const node = findNodeByKey(nodes, key);
        let allKeys = [key];
        if (node) {
          // get all the key belong this node, include the key itself
          allKeys = getAllKeys(key as string | number, node.children);
        }
        // remove these keys from tempKeys, checkedKeys and originTargetKeys
        removeElements(tempKeys, allKeys);
        removeElements(checkedKeys, allKeys);
        // removeElements(originTargetKeys, allKeys);
        // add the checked keys to checkedKeys
        checkedKeys.push(key);
        // until tempKeys is empty
        if (tempKeys.length === 0) {
          break;
        }
      }
      // the checkedKeys + originTargetKeys are the newTargetKeys
      // construct targets based on checkedKeys
      const selectedTargets = checkedKeys.map(
        (key) => findNodeByKey(nodes, key) as TreeNode
      );
      // construct targets based on oringinTargetKeys
      const originTargets = originPolicyTarget.filter((target: TreeNode) =>
        originTargetKeys.includes(target.key)
      );
      // const selectedTargets = allTargets[0].children.filter(
      //   (target: TreeNode) => keys.includes(target.key)
      // );
      newPolicyTargets.targets = [];
      newPolicyTargets.targetGroups = [];
      const convertedTargets = [...selectedTargets, ...originTargets]
        .filter((target) => !!target)
        .map((target: TreeNode) => {
          if (target.children) {
            return {
              title: target.title as string,
              key: target.key as string,
              folder: true,
              icon: 'user-team',
            };
          }
          return {
            ...target,
            title: target.title as string,
            key: target.key as string,
            icon: 'user',
          };
        });
      for (const target of convertedTargets) {
        if (target.folder) {
          newPolicyTargets.targetGroups.push({
            key: target.key as string,
            title: target.title as string,
          });
        } else {
          newPolicyTargets.targets.push({
            ...target,
            icon: undefined,
          });
        }
      }
      // remove empty
      if (newPolicyTargets.empty) {
        newPolicyTargets.empty = undefined;
      }
      newPolicyTargets.capacity = TargetCapacity.normal;
      setPolicy({ policyTarget: newPolicyTargets });
    },
    handleDeleteTarget: (key: Key) => {
      const policy = getPolicy();
      const { targets, targetGroups, targetType, empty } = policy.policyTarget;
      if (empty) {
        // no target user group, cannot delete anyone
        return;
      }
      if (key === 'all') {
        // delete all user, means no user
        // should  handle error here
        setPolicy({
          policyTarget: {
            targets: [],
            targetGroups: [],
            targetType,
            empty: true,
          },
        });
      }
      let newTargets = [];
      let newTargetGroups = [];
      // target
      newTargets = targets.filter((item: TargetUser) => item.key !== key);
      // target group
      newTargetGroups = targetGroups.filter(
        (item: TargetGroup) => item.key !== key
      );
      // 清空user和group应该设置error
      setPolicy({
        policyTarget: {
          targetType,
          targets: newTargets,
          targetGroups: newTargetGroups,
          empty:
            newTargets.length + newTargetGroups.length === 0 ? true : undefined,
          capacity:
            newTargets.length + newTargetGroups.length === 0
              ? TargetCapacity.empty
              : TargetCapacity.normal,
        },
      });
    },
    // save single state
    setGeneralState: (payload: Partial<PolicyItem>) => {
      setPolicy(payload);
    },
    validateGeneral: () => {
      const policy = getPolicy();
      const { policyName, policyPriority, policyTarget } = policy;
      if (!policyName) {
        return {
          policyName: {
            title: t(`policy.error.emptyPolicyNameTitle`),
            content: t('policy.error.emptyPolicyNameContent'),
          },
        };
      }
      if (!policyPriority) {
        return {
          policyPriority: {
            title: t(`policy.error.invalidPolicyPriorityTitle`),
            content: t('policy.error.invalidPolicyPriorityContent'),
          },
        };
      }
      if (
        policyTarget.empty ||
        policyTarget.capacity === TargetCapacity.empty
      ) {
        return {
          policyTarget: {
            title: t(`policy.error.emptyPolicyTargetTitle`),
            content: t('policy.error.emptyPolicyTargetContent'),
          },
        };
      }
    },
    // exchange inline
    // inline can only choose user to add, can not choose group
    // 2024-8-2  by Ant, now inline should be able to support  and auto get all user
    handleToRightTargetsInline: async (keys: Key[]) => {
      //Maximum number reached error
      const maximumUserError = () => {
        window.showNotification({
          severity: 'error',
          titleMsg: t('policy.error.inlineUserNumMaximumReached.title'),
          bodyMsg: t('policy.error.inlineUserNumMaximumReached.desc', {
            maxNum: INLINE_MAX_USER_NUMBER_IN_O365_GROUP,
          }),
        });
      };

      // check sum users in groups
      const groupIds = keys.filter((k) => !isEmail(k as string)) as string[];
      const data = (await UserApi.getUsersByGroupIds(groupIds)).data;
      if (
        data.error ||
        data.users.length > INLINE_MAX_USER_NUMBER_IN_O365_GROUP
      ) {
        maximumUserError();
        return;
      }

      const store = getAll();
      const policy = getPolicy();
      const allAvailableTargets = store.availableTargetsGerneral;
      const allSearchTargets = store.tempVars.searchTargetUserGroups;
      const allTargets = allSearchTargets.length
        ? allSearchTargets
        : allAvailableTargets;
      const setTempVars = store.setTempVars;
      const originPolicyTarget = policyTargetsInlineSelector(store);
      const originTargetKeys = originPolicyTarget.map(
        (target: TreeNode) => target.key
      );
      if (keys.length === 0) {
        return;
      }
      // do nothing when origin target is all user
      if (originTargetKeys.length === 1 && originTargetKeys[0] === 'all') {
        return;
      }
      let newPolicyTargets = cloneDeep(store.tempVars.targetUserGroups);
      // add all users
      if (keys.includes('all')) {
        newPolicyTargets = {
          targets: [],
          targetGroups: [],
          targetType: policy.policyTarget.targetType,
          capacity: TargetCapacity.all,
        };
        // Selected Targets panel reads from tempVars.targetUserGroups via
        // policyTargetsInlineSelector; writing to template.policyTarget only
        // (the previous behavior) left the UI panel empty. Mirror the Gmail
        // inline handler — write to tempVars so the panel renders "All Users".
        setTempVars({ targetUserGroups: newPolicyTargets });
        return;
      }
      // add user other than all
      // allTargets[0] is the top layer of the tree: all users / search users
      const nodes = allTargets[0].children;
      // only add user, filter the group
      const newKeys = keys.filter(
        (key: Key) =>
          !originTargetKeys.includes(key as string | number) &&
          checkEmailFormat(key as string)
      );
      const newTargets: TreeNode[] = newKeys.map(
        (key: Key) => findNodeByKey(nodes, key) as TreeNode
      );
      const newTargetsUser = newTargets.filter(
        (target: TreeNode) => !target.children
      );

      const targetsFromGroup: TreeNode[] = data.users.map((user) => {
        return {
          ...user,
        };
      });

      // deduplication
      const predicate = function () {
        const set = new Set();
        return (t: TreeNode) =>
          set.has(t.key) ? false : (set.add(t.key), true);
      };
      newPolicyTargets.targets = [
        ...originPolicyTarget,
        ...newTargetsUser,
        ...targetsFromGroup,
      ].filter(predicate());

      if (
        newPolicyTargets.targets.length > INLINE_MAX_USER_NUMBER_IN_O365_GROUP
      ) {
        maximumUserError();
        return;
      }

      newPolicyTargets.targetGroups = [];
      // remove empty
      if (newPolicyTargets.empty) {
        newPolicyTargets.empty = undefined;
      }
      newPolicyTargets.capacity = TargetCapacity.normal;
      setTempVars({ targetUserGroups: newPolicyTargets });
    },
    handleDeleteTargetInline: (key: Key) => {
      const store = getAll();
      const {
        setTempVars,
        tempVars: {
          targetUserGroups: { targets, targetGroups, targetType, empty },
        },
      } = store;
      if (empty) {
        // no target user group, cannot delete anyone
        return;
      }
      if (key === 'all') {
        // delete all user, means no user
        // should  handle error here
        setTempVars({
          targetUserGroups: {
            targets: [],
            targetGroups: [],
            targetType,
            empty: true,
          },
        });
      }
      let newTargets = [];
      let newTargetGroups = [];
      if (checkEmailFormat(key as string)) {
        // target
        newTargets = targets.filter((item: TargetUser) => item.key !== key);
        newTargetGroups = [...targetGroups];
      } else {
        // target group
        newTargetGroups = targetGroups.filter(
          (item: TargetGroup) => item.key !== key
        );
        newTargets = [...targets];
      }
      // 清空user和group应该设置error
      setTempVars({
        targetUserGroups: {
          targetType,
          targets: newTargets,
          targetGroups: newTargetGroups,
          empty:
            newTargets.length + newTargetGroups.length === 0 ? true : undefined,
          capacity:
            newTargets.length + newTargetGroups.length === 0
              ? TargetCapacity.empty
              : TargetCapacity.normal,
        },
      });
    },
    handleToRightTargetDomains: (keys: Key[]) => {
      const store = getAll();
      const {
        setTempVars,
        tempVars: { targetDomains, searchTargetDomains },
        availableTargetDomains: { targets },
      } = store;
      const allDomains = searchTargetDomains.length
        ? searchTargetDomains
        : targets;
      const newTargetDomains = cloneDeep(targetDomains);
      if (targetDomains.capacity === TargetCapacity.all) {
        return;
      }
      if (keys.includes('all')) {
        setTempVars({
          targetDomains: {
            targets: [],
            targetGroups: [],
            targetType: newTargetDomains.targetType,
            capacity: TargetCapacity.all,
          },
        });
        return;
      }
      const selectedDomains = allDomains.filter((domain: TargetUser) =>
        keys.includes(domain.key)
      );
      const originDomainKeys = newTargetDomains.targets.map(
        (domain: TargetUser) => domain.key
      );
      for (const domain of selectedDomains) {
        if (!originDomainKeys.includes(domain.key)) {
          newTargetDomains.targets.push(domain);
        }
      }
      // remove empty
      if (newTargetDomains.empty) {
        newTargetDomains.empty = undefined;
      }
      newTargetDomains.capacity = TargetCapacity.normal;
      // set target domains into tempVars
      setTempVars({ targetDomains: newTargetDomains });
    },
    handleDeleteTargetDomain: (key: Key) => {
      const store = getAll();
      const {
        setTempVars,
        tempVars: { targetDomains },
      } = store;
      const newTargetDomains = cloneDeep(targetDomains);
      const { targets, empty, targetType } = newTargetDomains;
      if (empty) {
        return;
      }
      if (key === 'all') {
        setTempVars({
          targetDomains: {
            targets: [],
            targetGroups: [],
            targetType,
            empty: true,
          },
        });
        return;
      }
      newTargetDomains.targets = targets.filter(
        (domain: TargetUser) => domain.key !== key
      );

      // set target domains into tempVars
      setTempVars({
        targetDomains: {
          ...newTargetDomains,
          empty: newTargetDomains.targets.length === 0 ? true : undefined,
          capacity:
            newTargetDomains.targets.length === 0
              ? TargetCapacity.empty
              : TargetCapacity.normal,
        },
      });
    },
    validateGeneralInline: () => {
      const policy = getPolicy();
      const {
        tempVars: { targetType, targetUserGroups, targetDomains },
        setGeneralState,
      } = getAll();
      const { policyName, policyPriority } = policy;
      if (!policyName) {
        return {
          policyName: {
            title: t(`policy.error.emptyPolicyNameTitle`),
            content: t('policy.error.emptyPolicyNameContent'),
          },
        };
      }
      if (!policyPriority) {
        return {
          policyPriority: {
            title: t(`policy.error.invalidPolicyPriorityTitle`),
            content: t('policy.error.invalidPolicyPriorityContent'),
          },
        };
      }
      if (targetType === TargetType.UserAndGroup) {
        if (
          targetUserGroups.empty ||
          targetUserGroups.capacity === TargetCapacity.empty
        ) {
          return {
            targetUserGroups: {
              title: t(`policy.error.emptyPolicyTargetTitle`),
              content: t('policy.error.emptyPolicyTargetContent'),
            },
          };
        }
      } else if (targetType === TargetType.Domain) {
        if (
          targetDomains.empty ||
          targetDomains.capacity === TargetCapacity.empty
        ) {
          return {
            targetDomains: {
              title: t(`policy.error.emptyPolicyTargetTitle`),
              content: t('policy.error.emptyPolicyTargetContent'),
            },
          };
        }
      }
      // pass the validation above, then set policy target
      const newPolicyTarget =
        targetType === TargetType.UserAndGroup
          ? targetUserGroups
          : targetDomains;
      setGeneralState({ policyTarget: newPolicyTarget });
    },
    validateGeneralSalesforce: () => {
      const policy = getPolicy();
      const {
        tempVars: { targetProfiles, targetCommunities, targetObjects },
        setGeneralState,
      } = getAll();
      const { policyName, policyPriority } = policy;
      if (!policyName) {
        return {
          policyName: {
            title: t(`policy.error.emptyPolicyNameTitle`),
            content: t('policy.error.emptyPolicyNameContent'),
          },
        };
      }
      if (!policyPriority) {
        return {
          policyPriority: {
            title: t(`policy.error.invalidPolicyPriorityTitle`),
            content: t('policy.error.invalidPolicyPriorityContent'),
          },
        };
      }
      if (
        (targetCommunities.empty ||
          targetCommunities.capacity === TargetCapacity.empty) &&
        (targetObjects.empty || targetObjects.capacity === TargetCapacity.empty)
      ) {
        return {
          targetObjects: {
            title: t(`policy.error.emptyPolicyTargetTitle`),
            content: t('policy.error.emptyPolicyTargetContent'),
          },
        };
      }

      if (
        targetProfiles.empty ||
        targetProfiles.capacity === TargetCapacity.empty
      ) {
        return {
          targetProfiles: {
            title: t(`policy.error.emptyPolicyTargetTitle`),
            content: t('policy.error.emptyPolicyTargetContent'),
          },
        };
      }
      const newPolicyTarget = {
        targetProfiles,
        targetCommunities,
        targetObjects,
      };
      setGeneralState({ policyTargetSalesforce: newPolicyTarget });
    },
    validateDlpSalesforce: () => {
      const policy = getPolicy();
      const {
        tempVars: { targetProfiles, targetObjects },
        setGeneralState,
      } = getAll();
      const { policyName, policyPriority } = policy;
      if (!policyName) {
        return {
          policyName: {
            title: t(`policy.error.emptyPolicyNameTitle`),
            content: t('policy.error.emptyPolicyNameContent'),
          },
        };
      }
      if (!policyPriority) {
        return {
          policyPriority: {
            title: t(`policy.error.invalidPolicyPriorityTitle`),
            content: t('policy.error.invalidPolicyPriorityContent'),
          },
        };
      }
      if (
        targetObjects.empty ||
        targetObjects.capacity === TargetCapacity.empty
      ) {
        return {
          targetObjects: {
            title: t(`policy.error.emptyPolicyTargetTitle`),
            content: t('policy.error.emptyPolicyTargetContent'),
          },
        };
      }
      if (
        targetProfiles.empty ||
        targetProfiles.capacity === TargetCapacity.empty
      ) {
        return {
          targetProfiles: {
            title: t(`policy.error.emptyPolicyTargetTitle`),
            content: t('policy.error.emptyPolicyTargetContent'),
          },
        };
      }
      const newPolicyTarget = {
        targetProfiles,
        targetObjects,
      };
      setGeneralState({ policyTargetSalesforce: newPolicyTarget });
    },
    // teams
    handleToRightTargetTeams: (keys: Key[]) => {
      const store = getAll();
      const policy = getPolicy();
      const {
        availableTargets: { targetGroups },
        tempVars: { searchTargetUserGroups },
      } = store;
      const allTargetGroups = searchTargetUserGroups.length
        ? searchTargetUserGroups[0].children
        : targetGroups;
      let newPolicyTargets = cloneDeep(policy.policyTarget);
      if (keys.includes('all')) {
        newPolicyTargets = {
          targets: [],
          targetGroups: [],
          targetType: newPolicyTargets.targetType,
          capacity: TargetCapacity.all,
        };
        setPolicy({
          policyTarget: newPolicyTargets,
        });
        return;
      }
      const selectedGroups = allTargetGroups.filter((group: TargetUser) =>
        keys.includes(group.key)
      );
      const originGroupKeys = newPolicyTargets.targetGroups.map(
        (group: TargetUser) => group.key
      );
      for (const group of selectedGroups) {
        if (!originGroupKeys.includes(group.key)) {
          newPolicyTargets.targetGroups.push(group);
        }
      }
      // remove empty
      if (newPolicyTargets.empty) {
        newPolicyTargets.empty = undefined;
      }
      newPolicyTargets.capacity = TargetCapacity.normal;
      // set policyTarget
      setPolicy({ policyTarget: newPolicyTargets });
    },
    handleDeleteTargetTeams: (key: Key) => {
      const policy = getPolicy();
      const { targetGroups, targetType, empty } = policy.policyTarget;
      if (empty) {
        // no target user group, cannot delete anyone
        return;
      }
      if (key === 'all') {
        // delete all user, means no user
        // should  handle error here
        setPolicy({
          policyTarget: {
            targets: [],
            targetGroups: [],
            targetType,
            empty: true,
          },
        });
      }
      // teamsTargets only contain groups
      const newTargets: TargetUser[] = [];
      let newTargetGroups = [];
      // target group
      newTargetGroups = targetGroups.filter(
        (item: TargetGroup) => item.key !== key
      );
      // 清空user和group应该设置error
      setPolicy({
        policyTarget: {
          targetType,
          targets: newTargets,
          targetGroups: newTargetGroups,
          empty:
            newTargets.length + newTargetGroups.length === 0 ? true : undefined,
          capacity:
            newTargets.length + newTargetGroups.length === 0
              ? TargetCapacity.empty
              : TargetCapacity.normal,
        },
      });
    },
    // gmail inline
    handleToRightTargetsGmailInline: (keys: Key[]) => {
      const store = getAll();
      const allAvailableTargets = store.availableTargetsGerneral;
      const allSearchTargets = store.tempVars.searchTargetUserGroups;
      const allTargets = allSearchTargets.length
        ? allSearchTargets
        : allAvailableTargets;
      const {
        setTempVars,
        tempVars: { targetType, targetUserGroups },
      } = store;
      const originPolicyTarget = policyTargetsInlineSelector(store);
      const originTargetKeys = originPolicyTarget.map(
        (target: TreeNode) => target.key
      );
      if (keys.length === 0) {
        return;
      }
      // do nothing when origin target is all user
      if (originTargetKeys.length === 1 && originTargetKeys[0] === 'all') {
        return;
      }
      let newPolicyTargets = cloneDeep(targetUserGroups);
      // add all users
      if (keys.includes('all')) {
        newPolicyTargets = {
          targets: [],
          targetGroups: [],
          targetType,
          capacity: TargetCapacity.all,
        };
        setTempVars({ targetUserGroups: newPolicyTargets });
        return;
      }
      // add user groups other than all
      // allTargets[0] is the top layer of the tree: all users
      const nodes = allTargets[0].children;
      const tempKeys = cloneDeep(keys);
      const checkedKeys = []; // the keys that will be added to newTargetKeys
      // if originTargetKeys and its children includes selectedKeys
      let expandOriginTargetKeys: string[] = [];
      originTargetKeys.forEach((key: Key) => {
        const node = findNodeByKey(nodes, key);
        if (node) {
          const allKeys = getAllKeys(key as string | number, node.children);
          expandOriginTargetKeys.push(...(allKeys as string[]));
        }
      });
      expandOriginTargetKeys = uniqueArr(expandOriginTargetKeys);
      // if selcted key existed in originTargetKeys already, remove it from tempKeys
      tempKeys.forEach((key: Key) => {
        if (expandOriginTargetKeys.includes(key as string)) {
          removeElements(tempKeys, [key]);
        }
      });
      const i = 0;
      while (i < tempKeys.length) {
        const key = tempKeys[i];
        // find this node by key
        const node = findNodeByKey(nodes, key);
        let allKeys = [key];
        if (node) {
          // get all the key belong this node, include the key itself
          allKeys = getAllKeys(key as string | number, node.children);
        }
        // remove these keys from tempKeys, checkedKeys and originTargetKeys
        removeElements(tempKeys, allKeys);
        removeElements(checkedKeys, allKeys);
        // removeElements(originTargetKeys, allKeys);
        // add the checked keys to checkedKeys
        checkedKeys.push(key);
        // until tempKeys is empty
        if (tempKeys.length === 0) {
          break;
        }
      }
      // the checkedKeys + originTargetKeys are the newTargetKeys
      // construct targets based on checkedKeys
      const selectedTargets = checkedKeys.map(
        (key) => findNodeByKey(nodes, key) as TreeNode
      );
      // construct targets based on oringinTargetKeys
      const originTargets = originPolicyTarget.filter((target: TreeNode) =>
        originTargetKeys.includes(target.key)
      );
      // const selectedTargets = allTargets[0].children.filter(
      //   (target: TreeNode) => keys.includes(target.key)
      // );
      newPolicyTargets.targets = [];
      newPolicyTargets.targetGroups = [];
      const convertedTargets = [...selectedTargets, ...originTargets]
        .filter((target) => !!target)
        .map((target: TreeNode) => {
          if (target.children) {
            return {
              title: target.title as string,
              key: target.key as string,
              folder: true,
              icon: 'user-team',
            };
          }
          return {
            ...target,
            title: target.title as string,
            key: target.key as string,
            icon: 'user',
          };
        });
      for (const target of convertedTargets) {
        if (target.folder) {
          newPolicyTargets.targetGroups.push({
            key: target.key as string,
            title: target.title as string,
          });
        } else {
          newPolicyTargets.targets.push({
            ...target,
            icon: undefined,
          });
        }
      }
      // remove empty
      if (newPolicyTargets.empty) {
        newPolicyTargets.empty = undefined;
      }
      newPolicyTargets.capacity = TargetCapacity.normal;
      setTempVars({ targetUserGroups: newPolicyTargets });
    },
    handleDeleteTargetGmailInline: (key: Key) => {
      const store = getAll();
      const {
        setTempVars,
        tempVars: {
          targetUserGroups: { targets, targetGroups, targetType, empty },
        },
      } = store;
      if (empty) {
        // no target user group, cannot delete anyone
        return;
      }
      if (key === 'all') {
        // delete all user, means no user
        // should  handle error here
        setTempVars({
          targetUserGroups: {
            targets: [],
            targetGroups: [],
            targetType,
            empty: true,
          },
        });
      }
      let newTargets = [];
      let newTargetGroups = [];
      // target
      newTargets = targets.filter((item: TargetUser) => item.key !== key);
      // target group
      newTargetGroups = targetGroups.filter(
        (item: TargetGroup) => item.key !== key
      );
      // 清空user和group应该设置error
      setTempVars({
        targetUserGroups: {
          targetType,
          targets: newTargets,
          targetGroups: newTargetGroups,
          empty:
            newTargets.length + newTargetGroups.length === 0 ? true : undefined,
          capacity:
            newTargets.length + newTargetGroups.length === 0
              ? TargetCapacity.empty
              : TargetCapacity.normal,
        },
      });
    },
    /**
     *  salesforce
     *  integration target objects and communities in one box
     *  no all choose options in objects and communities separately
     *  just one all choose option in the box
     */
    handleToRightTargetApps: (keys: Key[]) => {
      const store = getAll();
      const {
        setTempVars,
        tempVars: { targetObjects, searchTargetObjects, targetCommunities },
        availableTargetObjects: {
          targets: objectsTargets,
          targetGroups: objectsGroups,
        },
      } = store;
      const allObjects = searchTargetObjects.length
        ? searchTargetObjects[0]?.children || []
        : [...objectsTargets, ...objectsGroups];

      const newTargetObjects = cloneDeep(targetObjects);
      const newTargetCommunities = cloneDeep(targetCommunities);
      if (
        targetObjects.capacity === TargetCapacity.all &&
        targetCommunities.capacity === TargetCapacity.all
      ) {
        return;
      }
      // choose all
      if (keys.includes('all')) {
        setTempVars({
          targetObjects: {
            targets: [],
            targetGroups: [],
            targetType: TargetType.Objects,
            capacity: TargetCapacity.all,
          },
          targetCommunities: {
            targets: [],
            targetGroups: [],
            targetType: TargetType.Communities,
            capacity: TargetCapacity.all,
          },
        });
        return;
      }
      // del dup obj keys
      const selectedObjects = allObjects.filter((item: TargetUser) =>
        keys.includes(item.key)
      );
      // keys right tree
      const originObjectsKeys = newTargetObjects.targets.map(
        (object: TargetUser) => object.key
      );
      const originObjectsGroupKeys = newTargetObjects.targetGroups.map(
        (object: TargetGroup) => object.key
      );
      const originCommunitiesKeys = newTargetCommunities.targets.map(
        (community: TargetUser) => community.key
      );
      const originCommunitiesGroupKeys = newTargetCommunities.targetGroups.map(
        (community: TargetGroup) => community.key
      );

      for (const obj of selectedObjects) {
        if (
          ![...originObjectsKeys, ...originCommunitiesKeys].includes(obj.key)
        ) {
          if (obj.key === '00P' || obj.key === '500') {
            newTargetObjects.targets.push(obj);
          } else {
            newTargetCommunities.targets.push(obj);
          }
        } else if (
          ![...originObjectsGroupKeys, ...originCommunitiesGroupKeys].includes(
            obj.key
          )
        ) {
          newTargetCommunities.targetGroups.push(obj);
        }
      }

      // check is empty
      if (
        newTargetObjects.empty ||
        newTargetObjects.capacity === TargetCapacity.empty
      ) {
        if (
          newTargetObjects.targets.length !== 0 ||
          newTargetObjects.targetGroups.length !== 0
        ) {
          newTargetObjects.empty = undefined;
          newTargetObjects.capacity = TargetCapacity.normal;
        }
      }
      if (
        newTargetCommunities.empty ||
        newTargetCommunities.capacity === TargetCapacity.empty
      ) {
        if (
          newTargetCommunities.targets.length !== 0 ||
          newTargetCommunities.targetGroups.length !== 0
        ) {
          newTargetCommunities.empty = undefined;
          newTargetCommunities.capacity = TargetCapacity.normal;
        }
      }
      // set target object and community into tempVars
      setTempVars({
        targetObjects: newTargetObjects,
        targetCommunities: newTargetCommunities,
      });
    },
    handleDeleteTargetApps: (key: Key) => {
      const store = getAll();
      const {
        setTempVars,
        tempVars: {
          targetObjects: {
            targets: objectsTargets,
            targetGroups: objectsGroups,
            empty: objectsEmpty,
          },
          targetCommunities: {
            targets: communitiesTargets,
            targetGroups: communitiesGroups,
            empty: communitiesEmpty,
          },
        },
      } = store;
      console.log('communitiesGroups', communitiesGroups);
      if (objectsEmpty && communitiesEmpty) {
        // no target user group, cannot delete anyone
        return;
      }
      if (key === 'all') {
        // delete all user, means no user
        // should  handle error here
        setTempVars({
          targetObjects: {
            targets: [],
            targetGroups: [],
            empty: true,
            targetType: TargetType.Objects,
          },
          targetCommunities: {
            targets: [],
            targetGroups: [],
            empty: true,
            targetType: TargetType.Communities,
          },
        });
      }
      if (!objectsEmpty) {
        // for obj target
        let newObjectsTargets = [];
        let newObjectsTargetGroups = [];
        // target
        newObjectsTargets = objectsTargets.filter(
          (item: TargetUser) => item.key !== key
        );
        // target group
        newObjectsTargetGroups = objectsGroups.filter(
          (item: TargetGroup) => item.key !== key
        );
        setTempVars({
          targetObjects: {
            targets: newObjectsTargets,
            targetGroups: newObjectsTargetGroups,
            targetType: TargetType.Objects,
            empty:
              newObjectsTargets.length + newObjectsTargetGroups.length === 0
                ? true
                : undefined,
            capacity:
              newObjectsTargets.length + newObjectsTargetGroups.length === 0
                ? TargetCapacity.empty
                : TargetCapacity.normal,
          },
        });
      }
      if (!communitiesEmpty) {
        let newCommunitiesTargets = [];
        let newCommunitiesTargetGroups = [];
        // target
        newCommunitiesTargets = communitiesTargets.filter(
          (item: TargetUser) => item.key !== key
        );
        // target group
        newCommunitiesTargetGroups = communitiesGroups.filter(
          (item: TargetGroup) => item.key !== key
        );
        setTempVars({
          targetCommunities: {
            targets: newCommunitiesTargets,
            targetGroups: newCommunitiesTargetGroups,
            targetType: TargetType.Communities,
            empty:
              newCommunitiesTargets.length +
                newCommunitiesTargetGroups.length ===
              0
                ? true
                : undefined,
            capacity:
              newCommunitiesTargets.length +
                newCommunitiesTargetGroups.length ===
              0
                ? TargetCapacity.empty
                : TargetCapacity.normal,
          },
        });
      }
    },
    handleToRightTargetProfiles: (keys: Key[]) => {
      const store = getAll();
      const {
        setTempVars,
        tempVars: { targetProfiles, searchTargetProfiles },
        availableTargetProfiles: { targets },
      } = store;
      const allProfiles = searchTargetProfiles.length
        ? searchTargetProfiles[0]?.children || []
        : targets;
      const newTargetProfiles = cloneDeep(targetProfiles);
      if (targetProfiles.capacity === TargetCapacity.all) {
        return;
      }
      // choose all profiles
      if (keys.includes('all')) {
        setTempVars({
          targetProfiles: {
            targets: [],
            targetGroups: [],
            targetType: newTargetProfiles.targetType,
            capacity: TargetCapacity.all,
          },
        });
        return;
      }
      // del dup keys
      const selectedProfiles = allProfiles.filter((profile: TargetUser) =>
        keys.includes(profile.key)
      );
      const originProfileKeys = newTargetProfiles.targets.map(
        (profile: TargetUser) => profile.key
      );
      for (const profile of selectedProfiles) {
        if (!originProfileKeys.includes(profile.key)) {
          newTargetProfiles.targets.push(profile);
        }
      }
      // remove empty
      if (newTargetProfiles.empty) {
        newTargetProfiles.empty = undefined;
      }
      newTargetProfiles.capacity = TargetCapacity.normal;
      // set target domains into tempVars
      setTempVars({ targetProfiles: newTargetProfiles });
    },
    handleDeleteTargetProfiles: (key: Key) => {
      const store = getAll();
      const {
        setTempVars,
        tempVars: {
          targetProfiles: { targets, targetGroups, targetType, empty },
        },
      } = store;
      if (empty) {
        // no target user group, cannot delete anyone
        return;
      }
      if (key === 'all') {
        // delete all user, means no user
        // should  handle error here
        setTempVars({
          targetProfiles: {
            targets: [],
            targetGroups: [],
            targetType,
            empty: true,
          },
        });
      }
      let newTargets = [];
      let newTargetGroups = [];
      // target
      newTargets = targets.filter((item: TargetUser) => item.key !== key);
      // target group
      newTargetGroups = targetGroups.filter(
        (item: TargetGroup) => item.key !== key
      );
      // 清空user和group应该设置error
      setTempVars({
        targetProfiles: {
          targetType,
          targets: newTargets,
          targetGroups: newTargetGroups,
          empty:
            newTargets.length + newTargetGroups.length === 0 ? true : undefined,
          capacity:
            newTargets.length + newTargetGroups.length === 0
              ? TargetCapacity.empty
              : TargetCapacity.normal,
        },
      });
    },
    handleToRightTargetObjects: (keys: Key[]) => {
      const store = getAll();
      const {
        setTempVars,
        tempVars: { targetObjects, searchTargetObjects },
        availableTargetObjectsGeneral,
      } = store;
      const allTargets = searchTargetObjects.length
        ? searchTargetObjects
        : availableTargetObjectsGeneral;
      const originPolicyTarget = policyTargetsSalesforceObjectsSelector(store);
      const originTargetKeys = originPolicyTarget.map(
        (target: TreeNode) => target.key
      );
      if (keys.length === 0) {
        return;
      }
      // do nothing when origin target is all user
      if (originTargetKeys.length === 1 && originTargetKeys[0] === 'all') {
        return;
      }
      let newTargetObjects = cloneDeep(targetObjects);
      // add all users
      if (keys.includes('all')) {
        newTargetObjects = {
          targets: [],
          targetGroups: [],
          targetType: newTargetObjects.targetType,
          capacity: TargetCapacity.all,
        };
        setTempVars({ targetObjects: newTargetObjects });
        return;
      }
      // add user groups other than all
      // allTargets[0] is the top layer of the tree: all users
      const nodes = allTargets[0].children;
      const tempKeys = cloneDeep(keys);
      const checkedKeys = []; // the keys that will be added to newTargetKeys
      // if originTargetKeys and its children includes selectedKeys
      let expandOriginTargetKeys: string[] = [];
      originTargetKeys.forEach((key: Key) => {
        const node = findNodeByKey(nodes, key);
        if (node) {
          const allKeys = getAllKeys(key as string | number, node.children);
          expandOriginTargetKeys.push(...(allKeys as string[]));
        }
      });
      expandOriginTargetKeys = uniqueArr(expandOriginTargetKeys);
      // if selcted key existed in originTargetKeys already, remove it from tempKeys
      const tempKeysCopy = [...tempKeys];
      tempKeysCopy.forEach((key: Key) => {
        if (expandOriginTargetKeys.includes(key as string)) {
          removeElements(tempKeys, [key]);
        }
      });
      const i = 0;
      while (i < tempKeys.length) {
        const key = tempKeys[i];
        // find this node by key
        const node = findNodeByKey(nodes, key);
        let allKeys = [key];
        if (node) {
          // get all the key belong this node, include the key itself
          allKeys = getAllKeys(key as string | number, node.children);
        }
        // remove these keys from tempKeys, checkedKeys and originTargetKeys
        removeElements(tempKeys, allKeys);
        removeElements(checkedKeys, allKeys);
        // removeElements(originTargetKeys, allKeys);
        // add the checked keys to checkedKeys
        checkedKeys.push(key);
        // until tempKeys is empty
        if (tempKeys.length === 0) {
          break;
        }
      }
      // the checkedKeys + originTargetKeys are the newTargetKeys
      // construct targets based on checkedKeys
      const selectedTargets = checkedKeys.map(
        (key) => findNodeByKey(nodes, key) as TreeNode
      );
      // construct targets based on oringinTargetKeys
      const originTargets = originPolicyTarget.filter((target: TreeNode) =>
        originTargetKeys.includes(target.key)
      );
      // const selectedTargets = allTargets[0].children.filter(
      //   (target: TreeNode) => keys.includes(target.key)
      // );
      newTargetObjects.targets = [];
      newTargetObjects.targetGroups = [];
      const convertedTargets = [...selectedTargets, ...originTargets]
        .filter((target) => !!target)
        .map((target: TreeNode) => {
          if (target.children) {
            return {
              title: target.title as string,
              key: target.key as string,
              folder: true,
              icon: 'user-team',
            };
          }
          return {
            ...target,
            title: target.title as string,
            key: target.key as string,
            icon: 'user',
          };
        });
      for (const target of convertedTargets) {
        if (target.folder) {
          newTargetObjects.targetGroups.push({
            key: target.key as string,
            title: target.title as string,
          });
        } else {
          newTargetObjects.targets.push({
            ...target,
            icon: undefined,
          });
        }
      }
      // remove empty
      if (newTargetObjects.empty) {
        newTargetObjects.empty = undefined;
      }
      newTargetObjects.capacity = TargetCapacity.normal;
      setTempVars({ targetObjects: newTargetObjects });
    },
    handleDeleteTargetObjects: (key: Key) => {
      const store = getAll();
      const {
        setTempVars,
        tempVars: {
          targetObjects: { targets, targetGroups, targetType, empty },
        },
      } = store;
      if (empty) {
        // no target user group, cannot delete anyone
        return;
      }
      if (key === 'all') {
        // delete all user, means no user
        // should  handle error here
        setTempVars({
          targetObjects: {
            targets: [],
            targetGroups: [],
            targetType,
            empty: true,
          },
        });
      }
      let newTargets = [];
      let newTargetGroups = [];
      // target
      newTargets = targets.filter((item: TargetUser) => item.key !== key);
      // target group
      newTargetGroups = targetGroups.filter(
        (item: TargetGroup) => item.key !== key
      );
      // 清空user和group应该设置error
      setTempVars({
        targetObjects: {
          targetType,
          targets: newTargets,
          targetGroups: newTargetGroups,
          empty:
            newTargets.length + newTargetGroups.length === 0 ? true : undefined,
          capacity:
            newTargets.length + newTargetGroups.length === 0
              ? TargetCapacity.empty
              : TargetCapacity.normal,
        },
      });
    },
  };
};

export const policyTargetsSelector = (s: PolicyItem) => {
  const { t } = i18nInst;
  const { targets, targetGroups, empty, capacity } = s.policyTarget;
  const { policyType, policySubType } = s;
  let usersData: TargetUser[] = [];
  let groupsData: TargetGroup[] = [];
  const policyTypeName = getPolicyName(policyType, policySubType);
  // if empty is true, means the policy has not specified any user or group yet
  // if not submit, do not popup error
  if (empty) {
    return [];
  }
  // capacity is empty, means policy target is empty, should not happen
  if (capacity === TargetCapacity.empty) {
    return [
      {
        title: t(`policy.policyTarget.${policyTypeName}.none`),
        key: 'none',
      },
    ];
  }
  // all user
  if (capacity === TargetCapacity.all) {
    return [
      {
        title: t(`policy.policyTarget.${policyTypeName}.all`),
        key: 'all',
      },
    ];
  }
  usersData = targets;
  groupsData = targetGroups;
  const result = convertUserGroup(usersData, groupsData, s.policyType);
  return result;
};

// get target domain from tempVars
export const targetDomainsSelector = (s: PolicyStore) => {
  const { t } = i18nInst;
  const { targets, empty, capacity } = s.tempVars.targetDomains;
  // const { policyType, policySubType } = s.template;
  // const policyTypeName = getPolicyName(policyType, policySubType);
  // if empty is true, means the policy has not specified any user or group yet
  // if not submit, do not popup error
  console.log('targetDomainsSelector', s.tempVars.targetDomains);
  if (empty) {
    return [];
  }
  // capacity is empty, means policy target is empty, should not happen
  if (capacity === TargetCapacity.empty) {
    return [
      {
        title: t(`policy.policyTarget.noDomain`),
        key: 'none',
      },
    ];
  }
  // all user
  if (capacity === TargetCapacity.all) {
    return [
      {
        title: t(`policy.policyTarget.allDomains`),
        key: 'all',
      },
    ];
  }
  const result: TargetUser[] = targets.map((item) => ({
    ...item,
    icon: 'user',
  }));
  return result;
};

// get targetUserGroups from tempVars
export const policyTargetsInlineSelector = (s: PolicyStore) => {
  const { t } = i18nInst;
  const { targets, targetGroups, empty, capacity } =
    s.tempVars.targetUserGroups;
  const { policyType, policySubType } = s.template;
  let usersData: TargetUser[] = [];
  let groupsData: TargetGroup[] = [];
  const policyTypeName = getPolicyName(policyType, policySubType);
  // if empty is true, means the policy has not specified any user or group yet
  // if not submit, do not popup error
  if (empty) {
    return [];
  }
  // capacity is empty, means policy target is empty, should not happen
  if (capacity === TargetCapacity.empty) {
    return [
      {
        title: t(`policy.policyTarget.${policyTypeName}.none`),
        key: 'none',
      },
    ];
  }
  // all user
  if (capacity === TargetCapacity.all) {
    return [
      {
        title: t(`policy.policyTarget.${policyTypeName}.all`),
        key: 'all',
      },
    ];
  }
  usersData = targets;
  groupsData = targetGroups;
  const result = convertUserGroup(usersData, groupsData);
  return result;
};

// get targetProfiles from tempVars
export const policyTargetsSalesforceProfileSelector = (s: PolicyStore) => {
  const { t } = i18nInst;
  const { targets, targetGroups, empty, capacity } = s.tempVars.targetProfiles;
  const { policyType, policySubType } = s.template;
  let usersData: TargetUser[] = [];
  let groupsData: TargetGroup[] = [];
  const policyTypeName = getPolicyName(policyType, policySubType);
  // if empty is true, means the policy has not specified any user or group yet
  // if not submit, do not popup error
  if (empty) {
    return [];
  }
  // capacity is empty, means policy target is empty, should not happen
  if (capacity === TargetCapacity.empty) {
    return [
      {
        title: t(`policy.policyTarget.${policyTypeName}.noneProfile`),
        key: 'none',
      },
    ];
  }
  // all profile
  if (capacity === TargetCapacity.all) {
    return [
      {
        title: t(`policy.policyTarget.${policyTypeName}.allProfile`),
        key: 'all',
      },
    ];
  }
  usersData = targets;
  groupsData = targetGroups;
  const result = convertUserGroup(usersData, groupsData);
  return result;
};

export const policyTargetsSalesforceAppsSelector = (s: PolicyStore) => {
  const { t } = i18nInst;
  const {
    targets: objectTargets,
    targetGroups: objectGroups,
    // empty: objectEmpty,
    capacity: objectCapacity,
  } = s.tempVars.targetObjects;
  const {
    targets: communityTargets,
    targetGroups: communityGroups,
    // empty: communityEmpty,
    capacity: communityCapacity,
  } = s.tempVars.targetCommunities;
  const { policyType, policySubType } = s.template;
  let usersData: TargetUser[] = [];
  let groupsData: TargetGroup[] = [];
  let result: TreeNode[] = [];
  const policyTypeName = getPolicyName(policyType, policySubType);
  // if empty is true, means the policy has not specified any user or group yet
  // if not submit, do not popup error
  // if (objectEmpty.empty) {
  //   result.push([]);
  // }
  // if (communityGroups.empty) {
  //   return [];
  // }
  // all profile
  if (
    objectCapacity === TargetCapacity.all &&
    communityCapacity === TargetCapacity.all
  ) {
    return [
      {
        title: t(`policy.policyTarget.${policyTypeName}.allApps`),
        key: 'all',
      },
    ];
  }
  usersData = [...objectTargets, ...communityTargets];
  groupsData = [...objectGroups, ...communityGroups];
  result = convertUserGroup(usersData, groupsData);
  return result;
};

export const policyTargetsSalesforceObjectsSelector = (s: PolicyStore) => {
  const { t } = i18nInst;
  const { targets, targetGroups, empty, capacity } = s.tempVars.targetObjects;
  const { policyType, policySubType } = s.template;
  let usersData: TargetUser[] = [];
  let groupsData: TargetGroup[] = [];
  const policyTypeName = getPolicyName(policyType, policySubType);
  // if empty is true, means the policy has not specified any user or group yet
  // if not submit, do not popup error
  if (empty) {
    return [];
  }
  // capacity is empty, means policy target is empty, should not happen
  if (capacity === TargetCapacity.empty) {
    return [
      {
        title: t(`policy.policyTarget.${policyTypeName}.dlp.noneObjects`),
        key: 'none',
      },
    ];
  }
  // all profile
  if (capacity === TargetCapacity.all) {
    return [
      {
        title: t(`policy.policyTarget.${policyTypeName}.dlp.allObjects`),
        key: 'all',
      },
    ];
  }
  usersData = targets;
  groupsData = targetGroups;
  const result = convertUserGroup(usersData, groupsData);
  return result;
};

export const policyTypeNameSelector = (s: PolicyStore) => {
  const template = s.template;
  const { policyType, policySubType } = template;
  const policyTypeName = getPolicyName(policyType, policySubType);
  return policyTypeName;
};
